/**
 * Web Worker: decodifica o PNG de uma varredura, REPROJETA para a grade do
 * mapa (vizinho mais próximo, ver `geometria.ts`) e, se a tela não precisa do
 * tamanho inteiro, reduz com suavização — tudo fora da thread principal.
 *
 * A tabela de reprojeção (um Int32Array por radar e tamanho de PNG) é montada
 * uma vez e reaproveitada em todas as varreduras daquele radar.
 */
import { construirTabela, reprojetar, type GradeReprojecao } from './geometria';

export interface PedidoReprojecao {
  id: number;
  blob: Blob;
  /** Identifica a grade (radar + extensão): a tabela é guardada por chave. */
  chave: string;
  grade: GradeReprojecao;
  /** Largura final do bitmap (null = a da grade). */
  larguraFinal: number | null;
  /**
   * Pares [de, para] de cores empacotadas: a paleta própria de um radar
   * (Belém) trocada, classe por classe, pela comum (ver `produtos.ts`).
   */
  trocas: Uint32Array | null;
}

export type RespostaReprojecao = { id: number; ok: true; image: ImageBitmap } | { id: number; ok: false; error: string };

/** Tabelas guardadas: 11 radares × (talvez) dois tamanhos de PNG. */
const MAX_TABELAS = 24;
const tabelas = new Map<string, Int32Array>();

function tabelaPara(chave: string, grade: GradeReprojecao, w: number, h: number): Int32Array {
  const k = `${chave}|${w}x${h}`;
  let t = tabelas.get(k);
  if (!t) {
    t = construirTabela(grade, w, h);
    if (tabelas.size >= MAX_TABELAS) tabelas.delete(tabelas.keys().next().value!);
    tabelas.set(k, t);
  }
  return t;
}

type Canvas2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

/**
 * Bitmap sem conversão de cor nem pré-multiplicação: as cores são as classes
 * da legenda. Navegador que recusa as opções (Safari antigo) decodifica sem
 * elas — o alfa do PNG é binário, então a pré-multiplicação não altera cor.
 */
export async function bitmapFiel(blob: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(blob, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  } catch {
    return createImageBitmap(blob);
  }
}
type NovoCanvas = (w: number, h: number) => { canvas: OffscreenCanvas | HTMLCanvasElement; ctx: Canvas2D };

/**
 * O trabalho em si — exportado para a thread principal usar como reserva
 * (navegador sem OffscreenCanvas em worker), com o canvas que houver.
 */
export async function reprojetarBlob(
  blob: Blob,
  chave: string,
  grade: GradeReprojecao,
  larguraFinal: number | null,
  novoCanvas: NovoCanvas,
  trocas: Uint32Array | null = null,
): Promise<ImageBitmap> {
  const origem = await bitmapFiel(blob);
  const w = origem.width;
  const h = origem.height;
  if (w !== grade.origem[0] || h !== grade.origem[1]) {
    // Outro tamanho = outro sistema de radar, com outro tamanho de pixel: a
    // grade não vale para ele. Melhor sem radar neste quadro que no lugar errado.
    origem.close();
    throw new Error(`PNG de ${w}×${h}, esperado ${grade.origem[0]}×${grade.origem[1]}`);
  }
  let dados: ImageData;
  try {
    const { ctx } = novoCanvas(w, h);
    ctx.drawImage(origem, 0, 0);
    dados = ctx.getImageData(0, 0, w, h);
  } finally {
    origem.close();
  }
  const tabela = tabelaPara(chave, grade, w, h);
  const saida = new ImageData(grade.largura, grade.altura);
  const destino = new Uint32Array(saida.data.buffer);
  reprojetar(new Uint32Array(dados.data.buffer), tabela, destino);
  if (trocas) trocarCores(destino, trocas);

  const { canvas, ctx } = novoCanvas(grade.largura, grade.altura);
  ctx.putImageData(saida, 0, 0);
  if (!larguraFinal || larguraFinal >= grade.largura) return createImageBitmap(canvas);
  // Redução para a tela: suavização "high", como o mapa faria ao desenhar.
  const alturaFinal = Math.max(1, Math.round((grade.altura * larguraFinal) / grade.largura));
  const menor = novoCanvas(larguraFinal, alturaFinal);
  menor.ctx.imageSmoothingEnabled = true;
  menor.ctx.imageSmoothingQuality = 'high';
  menor.ctx.drawImage(canvas, 0, 0, larguraFinal, alturaFinal);
  return createImageBitmap(menor.canvas);
}

/** Troca cada cor `de` pela `para` (pares em `trocas`); as demais ficam como estão. */
export function trocarCores(pixels: Uint32Array, trocas: Uint32Array): void {
  const mapa = new Map<number, number>();
  for (let i = 0; i < trocas.length; i += 2) mapa.set(trocas[i], trocas[i + 1]);
  for (let k = 0; k < pixels.length; k++) {
    const p = pixels[k];
    if (p === 0) continue;
    const q = mapa.get(p);
    if (q !== undefined) pixels[k] = q;
  }
}

const offscreen: NovoCanvas = (w, h) => {
  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('OffscreenCanvas 2D indisponível');
  return { canvas, ctx };
};

// Só instala o manipulador quando roda COMO worker (o módulo também é
// importado pela thread principal, para a reserva).
const ehWorker =
  typeof self !== 'undefined' &&
  typeof (globalThis as { WorkerGlobalScope?: unknown }).WorkerGlobalScope !== 'undefined' &&
  self instanceof (globalThis as unknown as { WorkerGlobalScope: new () => unknown }).WorkerGlobalScope;

if (ehWorker) {
  const escopo = self as unknown as DedicatedWorkerGlobalScope;
  escopo.onmessage = async (e: MessageEvent<PedidoReprojecao>) => {
    const { id, blob, chave, grade, larguraFinal, trocas } = e.data;
    try {
      const image = await reprojetarBlob(blob, chave, grade, larguraFinal, offscreen, trocas);
      const res: RespostaReprojecao = { id, ok: true, image };
      escopo.postMessage(res, [image]);
    } catch (err) {
      const res: RespostaReprojecao = { id, ok: false, error: err instanceof Error ? err.message : String(err) };
      escopo.postMessage(res);
    }
  };
}
